import { useAuthStore } from '../../stores/authStore'
import { byChrono } from '../../utils/chatOrder'

/** Henüz sunucu listesine düşmemiş gönderiler. Önbellek tazelenince silinmesin. */
const pending = new Map()

function bucket(key) {
  if (!pending.has(key)) pending.set(key, [])
  return pending.get(key)
}

function maxNumericId(list) {
  let m = 0
  for (const x of list || []) {
    const n = Number(x?.id)
    if (Number.isFinite(n) && n > m) m = n
  }
  return m
}

function channelMaxId(qc, channelId) {
  let max = 0
  for (const [, data] of qc.getQueriesData({ queryKey: ['chat-messages', channelId] })) {
    max = Math.max(max, maxNumericId(data?.data?.messages))
  }
  return max
}

/** Gönderenin ekranına, sunucu yanıtını beklemeden düşen mesaj. */
export function localChatMessage(payload, { channelId, replyToId, sinceId } = {}) {
  const user = useAuthStore.getState().user || {}
  return {
    id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    pending: true,
    sinceId: sinceId || 0,
    channel_id: channelId ?? null,
    author_id: user.id,
    author_name: user.display_name || user.username || 'Sen',
    body: payload?.body || '',
    post_type: payload?.post_type || null,
    post_title: payload?.post_title || null,
    reply_to_id: replyToId || null,
    quote_msg_id: payload?.quote_msg_id || null,
    reply_count: 0,
    is_edited: 0,
    is_deleted: 0,
    created_at: new Date().toISOString(),
    attachments: [],
    reactions: [],
  }
}

function pushInto(old, msg) {
  if (!old?.data) return old
  const list = old.data.messages || []
  if (list.some((m) => m.id === msg.id)) return old
  return { ...old, data: { ...old.data, messages: [...list, msg] } }
}

function remember(key, msg) {
  const list = bucket(key)
  if (!list.some((m) => m.id === msg.id)) list.push(msg)
}

/**
 * Mesajı karşı tarafın almasını beklemeden açık sohbet önbelleğine yazar.
 * Taslak ve zamanlanmış gönderim akışta görünmez.
 */
export function showOutgoingMessage(qc, { channelId, payload, threadRootId, alsoChannel } = {}) {
  if (!payload || payload.is_draft || payload.scheduled_at) return null
  if (threadRootId) {
    const threadData = qc.getQueryData(['chat-thread', threadRootId])
    const msg = localChatMessage(payload, {
      channelId,
      replyToId: threadRootId,
      sinceId: maxNumericId(threadData?.data?.messages),
    })
    remember(`t:${threadRootId}`, msg)
    qc.setQueryData(['chat-thread', threadRootId], (old) => pushInto(old, msg))
    if (alsoChannel && channelId) {
      const copy = localChatMessage(payload, {
        channelId, sinceId: channelMaxId(qc, channelId),
      })
      copy.id = `${msg.id}-ch`
      remember(`c:${channelId}`, copy)
      qc.setQueriesData({ queryKey: ['chat-messages', channelId] }, (old) => pushInto(old, copy))
    }
    return msg.id
  }
  if (!channelId) return null
  const msg = localChatMessage(payload, { channelId, sinceId: channelMaxId(qc, channelId) })
  remember(`c:${channelId}`, msg)
  qc.setQueriesData({ queryKey: ['chat-messages', channelId] }, (old) => pushInto(old, msg))
  return msg.id
}

export function dropOutgoingMessage(qc, { channelId, threadRootId, tempId } = {}) {
  if (!tempId) return
  const forget = (key) => {
    const list = pending.get(key)
    if (!list) return
    pending.set(key, list.filter((m) => m.id !== tempId && m.id !== `${tempId}-ch`))
  }
  if (threadRootId) forget(`t:${threadRootId}`)
  if (channelId) forget(`c:${channelId}`)
  const drop = (old) => {
    if (!old?.data?.messages) return old
    const next = old.data.messages.filter((m) => m.id !== tempId && m.id !== `${tempId}-ch`)
    if (next.length === old.data.messages.length) return old
    return { ...old, data: { ...old.data, messages: next } }
  }
  if (threadRootId) qc.setQueryData(['chat-thread', threadRootId], drop)
  if (channelId) qc.setQueriesData({ queryKey: ['chat-messages', channelId] }, drop)
}

/** Sunucu listesine, hâlâ yolda olan kendi mesajlarını ekler. İneni listeden düşürür. */
function mergePending(key, messages) {
  const list = pending.get(key)
  if (!list?.length) return messages
  const real = (messages || []).filter((m) => !String(m.id).startsWith('tmp-'))
  const used = new Set()
  const kept = []
  for (const p of list) {
    const hit = real.find((m) =>
      !used.has(m.id)
      && String(m.author_id) === String(p.author_id)
      && (m.body || '') === (p.body || '')
      && Number(m.id) > Number(p.sinceId || 0))
    if (hit) used.add(hit.id)
    else kept.push(p)
  }
  pending.set(key, kept)
  if (!kept.length) return real
  const ids = new Set(real.map((m) => m.id))
  return [...real, ...kept.filter((p) => !ids.has(p.id))].sort(byChrono)
}

export function mergeOutgoing(channelId, messages) {
  if (channelId == null) return messages || []
  return mergePending(`c:${channelId}`, messages)
}

export function mergeThreadOutgoing(rootId, messages) {
  if (rootId == null) return messages || []
  return mergePending(`t:${rootId}`, messages)
}
